/**
 * The shift-log form (WT-2, fieldiq-api#1245), shared by the office page and
 * the phone. The two surfaces read different casings (office camelCase, the
 * /field surface snake_case); each maps its read into the plain shapes below,
 * so the form, its rules and the body it sends are one.
 *
 * A LOG IS ONE JOB, ONE DAY, ONE SHIFT (day or night). It carries the crew
 * present (ticked from the job's crew), the shift's hours and standby, the
 * safety and handover notes, and one row per unit — hours blank = not on the
 * log. The body always sends `crew_present` and `units`, and the API REPLACES
 * each set, so a row cleared here is a row removed there.
 *
 * - Hours and standby are 0–24 and together at most 24 (the API's rule).
 * - The hour-meter end is offered on an OWNED unit only (the API refuses it on
 *   a rent-in: meter_owned_only).
 * - A rent-in is offered only on a day it is on rent (the API refuses the
 *   rest: rent_in_not_on_rent), unless the log already holds it.
 */
import React, { useMemo, useState } from 'react';
import { blankToNull, toInput, wtDay } from '../../lib/well-testing';
import type { WtShiftLogWrite } from '../../types/well-testing';
import { WT_DATE_INPUT, WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtField, WtFormError } from './WtBits';

export interface LogCrewOption {
  userId: string;
  name: string;
  detail?: string | null;
}

export interface LogUnitOption {
  /** `owned:<equipmentId>` or `rent_in:<rentInId>`. */
  key: string;
  kind: 'owned' | 'rent_in';
  equipmentId: string | null;
  rentInId: string | null;
  name: string;
  detail: string | null;
  onRentOn?: string | null;
  offRentOn?: string | null;
}

export interface ShiftLogValues {
  crewPresent: { userId: string; name: string | null }[];
  hours: number | null;
  standbyHours: number | null;
  safetyNotes: string | null;
  handoverNote: string | null;
  notes: string | null;
  units: {
    kind: string;
    equipmentId: string | null;
    rentInId: string | null;
    name: string | null;
    hours: number | null;
    standbyHours: number | null;
    hourMeterEnd: number | null;
    notes: string | null;
  }[];
}

export interface UnitRow extends LogUnitOption {
  hours: string;
  standby: string;
  meter: string;
  notes: string;
}

export interface LogHeader {
  hours: string;
  standby: string;
  safety: string;
  handover: string;
  notes: string;
}

export const unitKey = (kind: string, equipmentId: string | null, rentInId: string | null) =>
  kind === 'rent_in' ? `rent_in:${rentInId}` : `owned:${equipmentId}`;

const onRentThat = (u: LogUnitOption, day: string) =>
  u.kind !== 'rent_in' || !day || ((!u.onRentOn || u.onRentOn <= day) && (!u.offRentOn || u.offRentOn >= day));

function hoursError(label: string, v: string): string | null {
  if (!v.trim()) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0 || n > 24) return `${label}: hours must be between 0 and 24.`;
  return null;
}

const over24 = (a: string, b: string) => (Number(a || 0) || 0) + (Number(b || 0) || 0) > 24;

/** The write body from the form. Exported for the test. */
export function shiftLogWriteBody(header: LogHeader, present: string[], units: UnitRow[]): WtShiftLogWrite {
  return {
    crew_present: present,
    hours: blankToNull(header.hours),
    standby_hours: blankToNull(header.standby),
    safety_notes: blankToNull(header.safety),
    handover_note: blankToNull(header.handover),
    notes: blankToNull(header.notes),
    units: units
      .filter((u) => u.hours.trim() !== '')
      .map((u) => ({
        ...(u.kind === 'rent_in' ? { rent_in_id: u.rentInId as string } : { equipment_id: u.equipmentId as string }),
        hours: u.hours.trim(),
        standby_hours: blankToNull(u.standby),
        // Owned units only — a rent-in never sends a meter.
        ...(u.kind === 'owned' ? { hour_meter_end: blankToNull(u.meter) } : {}),
        notes: blankToNull(u.notes),
      })),
  };
}

export function ShiftLogEditor({
  workDate,
  shift,
  shifts,
  onDayShift,
  values,
  crewOptions,
  unitOptions,
  pending,
  error,
  submitLabel,
  readOnly = false,
  onCancel,
  onSubmit,
}: {
  workDate: string;
  shift: string;
  /** Given (office, new or edit), the day and shift are inputs; absent (phone), they are fixed by the URL. */
  shifts?: { value: string; label: string }[];
  onDayShift?: (next: { workDate: string; shift: string }) => void;
  values: ShiftLogValues | null;
  crewOptions: LogCrewOption[];
  unitOptions: LogUnitOption[];
  pending: boolean;
  error: string | null;
  submitLabel: string;
  readOnly?: boolean;
  onCancel?: () => void;
  onSubmit: (body: WtShiftLogWrite) => void;
}) {
  const [header, setHeader] = useState<LogHeader>({
    hours: toInput(values?.hours),
    standby: values?.standbyHours ? toInput(values.standbyHours) : '',
    safety: values?.safetyNotes ?? '',
    handover: values?.handoverNote ?? '',
    notes: values?.notes ?? '',
  });
  const crewRows = useMemo(() => {
    const rows = new Map<string, LogCrewOption>();
    for (const c of crewOptions) rows.set(c.userId, c);
    for (const c of values?.crewPresent ?? []) if (!rows.has(c.userId)) rows.set(c.userId, { userId: c.userId, name: c.name ?? '—' });
    return Array.from(rows.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [present, setPresent] = useState<string[]>(() => (values?.crewPresent ?? []).map((c) => c.userId));
  const initialUnits = useMemo<UnitRow[]>(() => {
    const rows = new Map<string, UnitRow>();
    for (const u of unitOptions) if (onRentThat(u, workDate)) rows.set(u.key, { ...u, hours: '', standby: '', meter: '', notes: '' });
    for (const e of values?.units ?? []) {
      const key = unitKey(e.kind, e.equipmentId, e.rentInId);
      const opt = unitOptions.find((u) => u.key === key);
      rows.set(key, {
        key,
        kind: e.kind === 'rent_in' ? 'rent_in' : 'owned',
        equipmentId: opt?.equipmentId ?? e.equipmentId,
        rentInId: e.rentInId,
        name: e.name ?? opt?.name ?? '—',
        detail: opt?.detail ?? null,
        hours: toInput(e.hours),
        standby: e.standbyHours ? toInput(e.standbyHours) : '',
        meter: toInput(e.hourMeterEnd),
        notes: e.notes ?? '',
      });
    }
    return Array.from(rows.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [units, setUnits] = useState<UnitRow[]>(initialUnits);
  const [localError, setLocalError] = useState<string | null>(null);
  const setUnitRow = (i: number, patch: Partial<UnitRow>) => setUnits((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const togglePresent = (id: string) => setPresent((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const h = hoursError('Shift', header.hours) ?? hoursError('Standby', header.standby);
    if (h) return setLocalError(h);
    if (over24(header.hours, header.standby)) return setLocalError('Shift hours and standby together cannot be more than 24.');
    for (const u of units) {
      const err = hoursError(u.name, u.hours) ?? (u.hours.trim() ? hoursError(`${u.name} standby`, u.standby) : null);
      if (err) return setLocalError(err);
      if (u.hours.trim() && over24(u.hours, u.standby)) return setLocalError(`${u.name}: hours and standby together cannot be more than 24.`);
      if (!u.hours.trim() && u.meter.trim()) return setLocalError(`${u.name}: enter its hours, or clear the meter.`);
      if (u.meter.trim() && !/^\d+(\.\d+)?$/.test(u.meter.trim())) return setLocalError(`${u.name}: the hour meter must be a number.`);
    }
    onSubmit(shiftLogWriteBody(header, present, units));
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="wt-shift-log-form">
      <WtFormError message={localError ?? error} />
      <fieldset disabled={readOnly} className="space-y-4">
        {shifts && onDayShift && (
          <div className="grid gap-3 sm:grid-cols-4">
            <WtField label="Day">
              <input type="date" className={WT_DATE_INPUT} aria-label="Work day" value={workDate} onChange={(e) => onDayShift({ workDate: e.target.value, shift })} />
            </WtField>
            <WtField label="Shift">
              <select className={WT_INPUT} aria-label="Shift" value={shift} onChange={(e) => onDayShift({ workDate, shift: e.target.value })}>
                {shifts.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </WtField>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-4">
          <WtField label="Shift hours">
            <input className={WT_INPUT} inputMode="decimal" aria-label="Shift hours" value={header.hours} onChange={(e) => setHeader({ ...header, hours: e.target.value })} />
          </WtField>
          <WtField label="Standby hours">
            <input className={WT_INPUT} inputMode="decimal" aria-label="Standby hours" value={header.standby} onChange={(e) => setHeader({ ...header, standby: e.target.value })} />
          </WtField>
        </div>

        <section data-testid="wt-log-crew">
          <h3 className="mb-2 text-[0.68rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Crew present</h3>
          {crewRows.length === 0 ? (
            <p className="text-sm text-fieldiq-muted">No one is on this job&apos;s crew. Add the crew on the job first.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {crewRows.map((c) => (
                <label key={c.userId} className="flex min-h-[44px] items-center gap-3 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 px-3 py-2 text-sm text-fieldiq-text">
                  <input type="checkbox" aria-label={`${c.name} present`} checked={present.includes(c.userId)} onChange={() => togglePresent(c.userId)} />
                  <span className="min-w-0">
                    <span className="block truncate font-extrabold text-white">{c.name}</span>
                    {c.detail && <span className="block truncate text-xs text-fieldiq-muted">{c.detail}</span>}
                  </span>
                </label>
              ))}
            </div>
          )}
        </section>

        <section data-testid="wt-log-units">
          <h3 className="mb-2 text-[0.68rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Unit hours — leave blank if it did not run</h3>
          {units.length === 0 ? (
            <p className="text-sm text-fieldiq-muted">No owned unit is on this job and no rent-in is on rent on {wtDay(workDate)}.</p>
          ) : (
            <div className="space-y-2">
              {units.map((u, i) => (
                <div key={u.key} data-testid={`wt-log-unit-${u.kind}`} className="space-y-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-extrabold text-white">{u.name}</p>
                    <p className="text-xs text-fieldiq-muted">
                      {u.kind === 'rent_in' ? 'Rent-in' : 'Owned'}
                      {u.detail ? ` · ${u.detail}` : ''}
                    </p>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-4">
                    <WtField label="Hours">
                      <input className={WT_INPUT} inputMode="decimal" aria-label={`${u.name} hours`} value={u.hours} onChange={(e) => setUnitRow(i, { hours: e.target.value })} />
                    </WtField>
                    <WtField label="Standby">
                      <input className={WT_INPUT} inputMode="decimal" aria-label={`${u.name} standby hours`} value={u.standby} onChange={(e) => setUnitRow(i, { standby: e.target.value })} />
                    </WtField>
                    {u.kind === 'owned' && (
                      <WtField label="Hour meter at end">
                        <input className={WT_INPUT} inputMode="decimal" aria-label={`${u.name} hour meter end`} value={u.meter} onChange={(e) => setUnitRow(i, { meter: e.target.value })} />
                      </WtField>
                    )}
                    <WtField label="Notes">
                      <input className={WT_INPUT} aria-label={`${u.name} unit notes`} value={u.notes} onChange={(e) => setUnitRow(i, { notes: e.target.value })} />
                    </WtField>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <WtField label="Safety notes">
          <textarea className={`${WT_INPUT} h-20 py-2`} aria-label="Safety notes" value={header.safety} onChange={(e) => setHeader({ ...header, safety: e.target.value })} />
        </WtField>
        <WtField label="Handover to the next shift">
          <textarea className={`${WT_INPUT} h-20 py-2`} aria-label="Handover note" value={header.handover} onChange={(e) => setHeader({ ...header, handover: e.target.value })} />
        </WtField>
        <WtField label="Notes">
          <textarea className={`${WT_INPUT} h-16 py-2`} aria-label="Log notes" value={header.notes} onChange={(e) => setHeader({ ...header, notes: e.target.value })} />
        </WtField>
      </fieldset>

      {!readOnly && (
        <div className="flex flex-wrap justify-end gap-2">
          {onCancel && (
            <button type="button" className={WT_SECONDARY} onClick={onCancel} disabled={pending}>
              Cancel
            </button>
          )}
          <button type="submit" className={WT_PRIMARY} disabled={pending}>
            {pending ? 'Saving…' : submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}
